// Bảng nhập và tính của một mẫu trong kỳ lương, theo PayrollWorkbenchMatrix của V2: huy hiệu nguồn ở tiêu
// đề cột, ô nhập có khung và tự chèn dấu chấm hàng nghìn, Enter/↑/↓ đi dọc cột, bấm huy hiệu CT để soi
// công thức, hai cách xếp (ngang theo cột, dọc theo nhóm biến).
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Eye, X } from 'lucide-react';
import { extractPayrollDependencies } from '../core/formulaEvaluator';
import { NumberInput } from './ui';

export const SOURCE_BADGES = {
    NHAP_KY: { short: 'NT', label: 'Nhập trong kỳ', className: 'bg-amber-100 text-amber-800' },
    NANG_SUAT: { short: 'NS', label: 'Năng suất', className: 'bg-orange-100 text-orange-700' },
    THOA_THUAN: { short: 'TT', label: 'Thỏa thuận', className: 'bg-green-100 text-green-700' },
    CONG_THUC: { short: 'CT', label: 'Công thức', className: 'bg-indigo-100 text-indigo-700' },
    HE_THONG: { short: 'HT', label: 'Hệ thống', className: 'bg-gray-100 text-gray-600' },
    UNG_LUONG: { short: 'UL', label: 'Ứng lương', className: 'bg-rose-100 text-rose-700' },
    PHEP_NAM: { short: 'PN', label: 'Phép năm', className: 'bg-sky-100 text-sky-700' },
    NGAY_LE: { short: 'NL', label: 'Ngày lễ', className: 'bg-sky-100 text-sky-700' },
    BHXH_THUE: { short: 'BT', label: 'BHXH và thuế', className: 'bg-gray-100 text-gray-600' },
};

const badgeOf = (source) => SOURCE_BADGES[source] ?? SOURCE_BADGES.HE_THONG;

export const SORT_OPTIONS = [
    { value: 'default', label: 'Thứ tự mặc định' },
    { value: 'name-asc', label: 'Tên A → Z' },
    { value: 'name-desc', label: 'Tên Z → A' },
    { value: 'code-asc', label: 'Mã tăng dần' },
    { value: 'code-desc', label: 'Mã giảm dần' },
    { value: 'net-desc', label: 'Thực chi cao → thấp' },
    { value: 'net-asc', label: 'Thực chi thấp → cao' },
];

/** Sắp dòng theo lựa chọn ở thanh tab; dùng chung cho bảng Tổng hợp và bảng từng mẫu. */
export function sortRows(rows, sort, net) {
    if (!sort || sort === 'default') return rows;
    const [key, direction] = sort.split('-');
    const factor = direction === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => {
        if (key === 'net') return ((net(left) || 0) - (net(right) || 0)) * factor;
        const pick = (row) => String(key === 'code' ? row.soHd : row.hoTen);
        return pick(left).localeCompare(pick(right), 'vi', { numeric: true }) * factor;
    });
}

const GROUP_TONES = ['bg-indigo-50', 'bg-orange-50', 'bg-amber-50', 'bg-green-50', 'bg-purple-50', 'bg-blue-50', 'bg-rose-50', 'bg-teal-50'];
// Tổng cộng và Thực chi là hai số kế toán dò nhiều nhất, tô riêng để mắt bắt được ngay.
const EMPHASIS = { TC: 'bg-gray-100 font-semibold text-gray-900', TCHI: 'bg-green-50 font-bold text-green-800' };

export function formatCellValue(value, dataType) {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);
    if (number === 0) return '';
    return number.toLocaleString('vi-VN', { maximumFractionDigits: dataType === 'TIEN' ? 0 : 4 });
}

// Các cột liền nhau cùng nhóm biến gộp thành một ô tiêu đề nhóm.
function groupSpans(columns) {
    const spans = [];
    for (const column of columns) {
        const last = spans[spans.length - 1];
        if (last && last.group === column.group) last.columns.push(column);
        else spans.push({ group: column.group, columns: [column] });
    }
    return spans;
}

function SourceBadge({ column, inspected, onInspect }) {
    const meta = badgeOf(column.source);
    const base = `inline-flex items-center justify-center h-4 px-1 rounded text-[10px] font-bold leading-none flex-shrink-0 ${meta.className}`;
    if (column.source === 'CONG_THUC' && column.formula && onInspect) {
        return (
            <button type="button" onClick={(event) => { event.stopPropagation(); onInspect(column); }} title={`Bấm để soi công thức: ${column.formula}`}
                className={`${base} cursor-pointer ${inspected ? 'ring-2 ring-indigo-500' : 'hover:opacity-80'}`}>{meta.short}</button>
        );
    }
    return <span className={base} title={meta.label}>{meta.short}</span>;
}

// Tiêu đề cột dính trên đầu khi cuộn nên nền phải đặc (không trong suốt), nếu không số của các dòng
// cuộn qua bên dưới sẽ lộ xuyên lên tiêu đề.
const HEADER_EMPHASIS = { TC: 'bg-gray-100', TCHI: 'bg-green-50' };
function headerTone({ inspected, referenced, code }) {
    if (inspected) return 'bg-indigo-100 ring-1 ring-inset ring-indigo-500';
    if (referenced) return 'bg-indigo-50 ring-1 ring-inset ring-indigo-300';
    return HEADER_EMPHASIS[code] ?? 'bg-gray-50';
}

/** Nền và viền của một ô: cảnh báo > đang soi > được công thức đang soi dùng tới > ghi đè > nhấn mạnh. */
function cellTone({ warning, inspected, referenced, overridden, code }) {
    const background = warning ? 'bg-yellow-50' : inspected ? 'bg-indigo-50' : referenced ? 'bg-indigo-50/50' : overridden ? 'bg-orange-50' : EMPHASIS[code] ?? '';
    const ring = inspected ? 'ring-1 ring-inset ring-indigo-500' : referenced ? 'ring-1 ring-inset ring-indigo-300' : '';
    return `${background} ${ring}`;
}

function CellInput({ row, column, cell, rowIndex, raw, invalid, onInputChange }) {
    // Enter và mũi tên lên/xuống nhảy sang cùng cột của người kế tiếp: nhập lương là đi dọc một cột
    // cho cả trăm người, bắt cầm chuột bấm từng ô là chỗ chậm nhất của màn này.
    const handleKeyDown = (event) => {
        const step = event.key === 'Enter' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
        if (!step) return;
        const target = document.querySelector(`[data-payroll-row="${rowIndex + step}"][data-payroll-col="${column.code}"]`);
        if (!target) return;
        event.preventDefault();
        target.focus();
        target.select();
    };
    const dirty = raw !== undefined;
    return (
        <NumberInput
            value={dirty ? raw : (cell.value ?? '')}
            onChange={(next) => onInputChange(row.soHd, column.code, next)}
            onKeyDown={handleKeyDown}
            onFocus={(event) => event.target.select()}
            data-payroll-row={rowIndex}
            data-payroll-col={column.code}
            aria-label={`${column.code} ${row.hoTen}`}
            className={`w-full h-8 px-2 text-right text-sm rounded-md border bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                ${invalid ? 'border-red-500 text-red-700' : dirty ? 'border-indigo-500 bg-indigo-50 font-semibold text-indigo-800' : 'border-gray-300'}`}
        />
    );
}

/** Ô tính ra. Số ghi đè từ trước (nếu có) vẫn hiện dấu GĐ để không lẫn với số tính ra. */
function CellValue({ column, cell }) {
    return (
        <span className="flex items-center justify-end gap-1 whitespace-nowrap"
            title={cell.overridden ? `Đã ghi đè (số tính ra ${formatCellValue(cell.original, column.dataType) || 0}): ${cell.overrideReason || ''}` : undefined}>
            {cell.overridden && <span className="px-1 rounded bg-orange-200 text-orange-800 text-[10px] font-bold flex-shrink-0">GĐ</span>}
            <span className={cell.overridden ? 'font-semibold text-orange-800' : ''}>{formatCellValue(cell.value, column.dataType)}</span>
        </span>
    );
}

function EmployeeCell({ row, stt, onRowClick }) {
    return (
        <button type="button" onClick={() => onRowClick(row)} className="w-full flex items-start gap-2 text-left" title="Xem cách tính">
            <span className="w-6 flex-shrink-0 text-right text-xs text-gray-400 mt-0.5">{stt}</span>
            <span className="min-w-0">
                <span className="flex items-center gap-1 font-medium text-gray-900 hover:text-indigo-600">
                    {row.warnings.length > 0 && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" title={row.warnings.map((warning) => warning.message).join('\n')} />}
                    {row.hoTen}
                </span>
                <span className="block text-xs text-gray-500">{row.soHd} · {row.to || 'Chưa phân tổ'}</span>
            </span>
        </button>
    );
}

export default function WorkbenchMatrix({
    group, rows, pendingRaw, invalidCells, layout = 'horizontal', showOnlyInputs = false, onInputChange, onRowClick,
}) {
    const [inspected, setInspected] = useState(null);
    const toggleInspect = (column) => setInspected((current) => (current?.code === column.code ? null : column));
    const referenced = useMemo(() => new Set(inspected?.formula ? extractPayrollDependencies(inspected.formula) : []), [inspected]);
    const columns = showOnlyInputs ? group.columns.filter((column) => column.allowInput) : group.columns;
    const spans = groupSpans(columns);

    const cellWarning = (row, code) => row.warnings.filter((warning) => warning.variableCode === code);
    const renderCell = (row, column, rowIndex) => {
        const cell = row.cells[column.code];
        if (cell.editable) {
            return <CellInput row={row} column={column} cell={cell} rowIndex={rowIndex} raw={pendingRaw[row.soHd]?.[column.code]}
                invalid={invalidCells.has(`${row.soHd}|${column.code}`)} onInputChange={onInputChange} />;
        }
        return <CellValue column={column} cell={cell} />;
    };
    const toneOf = (row, column) => cellTone({
        warning: cellWarning(row, column.code).length > 0,
        inspected: inspected?.code === column.code,
        referenced: referenced.has(column.code),
        overridden: row.cells[column.code].overridden,
        code: column.code,
    });

    const stickyHead = 'border-b border-r border-gray-200 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider';
    const viewCell = (row) => (
        <td className={`px-2 py-1.5 border-b border-l border-gray-200 text-center sticky right-0 bg-white z-10 ${layout === 'vertical' ? 'align-top' : ''}`}>
            <button type="button" onClick={() => onRowClick(row)} title="Xem cách tính" aria-label={`Xem ${row.hoTen}`} className="p-1.5 rounded-full text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50">
                <Eye className="w-4 h-4" />
            </button>
        </td>
    );

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            {inspected && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-indigo-50 border-b border-indigo-200 text-sm text-indigo-800">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="px-1.5 rounded bg-indigo-100 text-indigo-700 text-xs font-bold">CT</span>
                        <span className="font-semibold">{inspected.name} ({inspected.code})</span>
                        <code className="font-mono text-xs bg-white px-2 py-0.5 rounded">= {inspected.formula}</code>
                        <span className="text-xs text-indigo-600">Các cột được dùng tới đang viền xanh.</span>
                    </div>
                    <button type="button" onClick={() => setInspected(null)} className="p-1 rounded hover:bg-indigo-100" title="Bỏ soi" aria-label="Bỏ soi công thức"><X className="w-4 h-4" /></button>
                </div>
            )}
            <div className="overflow-auto max-h-[68vh]">
                <table className="text-sm border-separate border-spacing-0 min-w-full">
                    {layout === 'horizontal' ? (
                        <>
                            <thead className="sticky top-0 z-20">
                                <tr>
                                    <th rowSpan={2} className={`${stickyHead} px-3 py-2 min-w-[220px] sticky left-0 z-30 text-left`}>Nhân sự</th>
                                    {spans.map((span, index) => (
                                        <th key={`${span.group}-${index}`} colSpan={span.columns.length} className={`${GROUP_TONES[index % GROUP_TONES.length]} border-b border-r border-gray-200 px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wider text-gray-700`}>
                                            {span.group}
                                        </th>
                                    ))}
                                    <th rowSpan={2} className={`${stickyHead} px-2 py-2 w-12 sticky right-0 z-30 text-center border-l`}>Xem</th>
                                </tr>
                                <tr>
                                    {columns.map((column) => (
                                        <th key={column.code} title={`${column.name} (${column.code})\nNguồn: ${badgeOf(column.source).label}${column.formula ? `\nCông thức: ${column.formula}` : ''}`}
                                            className={`border-b border-r border-gray-200 px-2 py-1.5 min-w-[112px] max-w-[160px] text-right align-top ${headerTone({ inspected: inspected?.code === column.code, referenced: referenced.has(column.code), code: column.code })}`}>
                                            <div className="flex items-start gap-1.5 text-left">
                                                <SourceBadge column={column} inspected={inspected?.code === column.code} onInspect={toggleInspect} />
                                                <span className="text-[11px] font-semibold text-gray-700 leading-tight normal-case">{column.name}</span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {rows.map((row, index) => (
                                    <tr key={row.soHd} className="hover:bg-gray-50/60">
                                        <td className="px-3 py-1.5 border-b border-r border-gray-200 sticky left-0 bg-white z-10"><EmployeeCell row={row} stt={index + 1} onRowClick={onRowClick} /></td>
                                        {columns.map((column) => {
                                            const warnings = cellWarning(row, column.code);
                                            return (
                                                <td key={column.code} title={warnings.length ? warnings.map((warning) => warning.message).join('\n') : undefined}
                                                    className={`border-b border-r border-gray-200 text-right text-gray-800 ${row.cells[column.code].editable ? 'px-1.5 py-1' : 'px-3 py-1.5'} ${toneOf(row, column)}`}>
                                                    {renderCell(row, column, index)}
                                                </td>
                                            );
                                        })}
                                        {viewCell(row)}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="sticky bottom-0 z-20">
                                <tr className="bg-gray-100 font-semibold text-gray-900">
                                    <td className="px-3 py-2 border-t border-r border-gray-300 sticky left-0 bg-gray-100">TỔNG ({rows.length})</td>
                                    {columns.map((column) => (
                                        <td key={column.code} className={`px-3 py-2 border-t border-r border-gray-300 text-right whitespace-nowrap ${column.code === 'TCHI' ? 'text-green-800' : ''}`}>
                                            {column.additive ? formatCellValue(rows.reduce((sum, row) => sum + (Number(row.cells[column.code].value) || 0), 0), column.dataType) : ''}
                                        </td>
                                    ))}
                                    <td className="border-t border-l border-gray-300 sticky right-0 bg-gray-100" />
                                </tr>
                            </tfoot>
                        </>
                    ) : (
                        <>
                            <thead className="sticky top-0 z-20">
                                <tr>
                                    <th className={`${stickyHead} px-3 py-2 min-w-[220px] sticky left-0 z-30 text-left`}>Nhân sự</th>
                                    {spans.map((span, index) => (
                                        <th key={`${span.group}-${index}`} className={`${GROUP_TONES[index % GROUP_TONES.length]} min-w-[300px] border-b border-r border-gray-200 px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wider text-gray-700`}>{span.group}</th>
                                    ))}
                                    <th className={`${stickyHead} px-2 py-2 w-12 sticky right-0 z-30 text-center border-l`}>Xem</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {rows.map((row, index) => (
                                    <tr key={row.soHd} className="align-top hover:bg-gray-50/60">
                                        <td className="px-3 py-2 border-b border-r border-gray-200 sticky left-0 bg-white z-10 align-top"><EmployeeCell row={row} stt={index + 1} onRowClick={onRowClick} /></td>
                                        {spans.map((span, spanIndex) => (
                                            <td key={`${span.group}-${spanIndex}`} className="border-b border-r border-gray-200 p-2 align-top">
                                                <div className="space-y-1">
                                                    {span.columns.map((column) => {
                                                        const warnings = cellWarning(row, column.code);
                                                        return (
                                                            <div key={column.code} title={warnings.length ? warnings.map((warning) => warning.message).join('\n') : undefined}
                                                                className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs ${toneOf(row, column)}`}>
                                                                <span className="flex flex-1 min-w-0 items-start gap-1.5 font-medium text-gray-700">
                                                                    <SourceBadge column={column} inspected={inspected?.code === column.code} onInspect={toggleInspect} />
                                                                    <span className="leading-tight break-words">{column.name}</span>
                                                                    {warnings.length > 0 && <AlertTriangle className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
                                                                </span>
                                                                <span className="w-32 flex-shrink-0 text-right text-sm">{renderCell(row, column, index)}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        ))}
                                        {viewCell(row)}
                                    </tr>
                                ))}
                            </tbody>
                        </>
                    )}
                </table>
                {rows.length === 0 && <div className="p-10 text-center text-sm text-gray-400">Không có nhân sự phù hợp bộ lọc.</div>}
            </div>
        </div>
    );
}
